import { createContext, ReactNode, useContext, useLayoutEffect, useMemo } from 'react';
import { Link, LinkProps, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface NavigationMemory { positions: Map<string, { top: number; left: number }>; destinations: Map<string, string> }
const MemoryContext = createContext<NavigationMemory | null>(null);
export function ViewerNavigationProvider({ children }: { children: ReactNode }) {
  const memory = useMemo(() => ({ positions: new Map<string, { top: number; left: number }>(), destinations: new Map<string, string>() }), []);
  return <MemoryContext.Provider value={memory}>{children}</MemoryContext.Provider>;
}

export function ViewerLink({ state, ...props }: LinkProps) {
  const location = useLocation();
  return <Link {...props} state={{ ...state, viewerFrom: location.pathname + location.search }} />;
}

export function ViewerBack({ fallback }: { fallback: string }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const prefix = location.pathname.split('/').slice(0, 3).join('/') + '/';
  const from = location.state?.viewerFrom;
  return <button type="button" className="rv-button" onClick={() => {
    if (typeof from === 'string' && from.startsWith(prefix) && window.history.state?.idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  }}><ArrowLeft size={18} aria-hidden="true" />{t('recipeViewer.back')}</button>;
}

export function useViewerScroll(elementId = 'recipeViewerScroll') {
  const location = useLocation();
  const memory = useContext(MemoryContext);
  const scrollParams = new URLSearchParams(location.search);
  // Expansion and incremental rendering retain the current reading position.
  scrollParams.delete('expanded');
  scrollParams.delete('limit');
  const panel = elementId === 'recipeViewerLeft' || elementId === 'recipeViewerRight';
  const key = elementId + ':' + location.pathname + (panel ? '' : scrollParams.toString());
  useLayoutEffect(() => {
    const element = document.getElementById(elementId);
    if (!element || !memory) return;
    const position = memory.positions.get(key) ?? { top: 0, left: 0 };
    element.scrollTop = position.top;
    element.scrollLeft = position.left;
    const save = () => memory.positions.set(key, { top: element.scrollTop, left: element.scrollLeft });
    element.addEventListener('scroll', save, { passive: true });
    return () => { save(); element.removeEventListener('scroll', save); };
  }, [key, memory, elementId]);
}

export function useViewerLandingPath(path: string) {
  const memory = useContext(MemoryContext);
  const location = useLocation();
  // Only remember tool/list landings, never replace a tab destination with a detail.
  useLayoutEffect(() => {
    if (location.pathname === path) memory?.destinations.set(path, path + location.search);
  }, [location.pathname, location.search, path, memory]);
  return location.pathname === path ? path + location.search : memory?.destinations.get(path) ?? path;
}
